import React, { useMemo, useCallback, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Globe, Share2, Copy, Check, ArrowRight, TrendingDown,
  Download, Twitter, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useBrand } from '@/config/brands';
import { loadStaticData } from '@/lib/offlineData';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ProcedurePrice { us_code: string; country_code: string; price_usd: number; }
interface ProcedureName { code: string; name: string; llm_quick_name?: string; }
interface USPrice { cpt_code: string; avg_gross_charge: number; }
interface CountryInfo { country_code: string; country_name: string; flag_emoji: string; }

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatUSD(n: number): string {
  if (!n || !isFinite(n)) return '$0';
  if (n >= 10000) return `$${(n / 1000).toFixed(0)}k`;
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}

// Pre-selected viral-worthy procedures (high US cost, widely mapped)
const VIRAL_PROCEDURES = [
  '27447', // Knee Replacement
  '27130', // Hip Replacement
  '59510', // C-Section
  '66984', // Cataract Surgery
  '47562', // Gallbladder Removal
  '43239', // Upper GI Endoscopy
  '45380', // Colonoscopy w/ Biopsy
  '70553', // Brain MRI
  '74177', // CT Abdomen
  '99213', // Office Visit
  '99285', // ER Visit
  '93000', // ECG
  '85025', // CBC
  '80053', // Metabolic Panel
  '29881', // Knee Arthroscopy
  '43235', // Diagnostic EGD
  '49505', // Hernia Repair
  '19301', // Mastectomy Partial
  '33533', // CABG
  '50360', // Kidney Transplant
];

// ─── Single Viral Card Component ────────────────────────────────────────────

function ViralCard({
  procedureCode,
  procedureName,
  usPrice,
  topCountries,
  brandName,
  brandUrl,
  onShare,
}: {
  procedureCode: string;
  procedureName: string;
  usPrice: number;
  topCountries: { flag: string; name: string; price: number; savings: number; code: string }[];
  brandName: string;
  brandUrl: string;
  onShare: (text: string, url: string) => void;
}) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${brandUrl}/international/code-explorer/${procedureCode}`;
  const cheapest = topCountries[0];

  const shareText = cheapest
    ? `${procedureName}: $${usPrice >= 1000 ? (usPrice/1000).toFixed(0) + 'k' : usPrice.toFixed(0)} in the US, but only $${cheapest.price >= 1000 ? (cheapest.price/1000).toFixed(1) + 'k' : cheapest.price.toFixed(0)} in ${cheapest.name}. That's ${cheapest.savings}% less.\n\nSee all 32 countries:`
    : '';

  const handleCopy = () => {
    navigator.clipboard.writeText(`${shareText}\n${shareUrl}`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=550,height=420');
  };

  return (
    <div className="rounded-2xl border border-border/40 overflow-hidden bg-card hover:border-primary/30 transition-all">
      {/* Card Header — the shareable visual */}
      <div className="bg-gradient-to-br from-red-500/10 via-background to-green-500/10 p-6">
        <p className="text-xs text-muted-foreground mb-1 font-mono">{procedureCode}</p>
        <h3 className="text-lg font-bold mb-4">{procedureName}</h3>

        {/* US Price — big and red */}
        <div className="flex items-center gap-3 mb-5">
          <span className="text-2xl">🇺🇸</span>
          <div className="flex-1">
            <p className="text-xs text-muted-foreground">United States</p>
            <p className="text-2xl font-bold text-red-400">{formatUSD(usPrice)}</p>
          </div>
        </div>

        {/* Top 5 cheapest countries */}
        <div className="space-y-2">
          {topCountries.slice(0, 5).map((c, idx) => (
            <div key={c.code} className="flex items-center gap-3">
              <span className="text-lg">{c.flag}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{c.name}</p>
              </div>
              <p className="font-semibold text-green-400">{formatUSD(c.price)}</p>
              <Badge variant="outline" className="text-[10px] text-green-400/80 border-green-500/30 shrink-0">
                -{c.savings}%
              </Badge>
            </div>
          ))}
        </div>

        {topCountries.length > 5 && (
          <p className="text-xs text-muted-foreground mt-3 text-center">
            +{topCountries.length - 5} more countries
          </p>
        )}
      </div>

      {/* Card Footer — share actions */}
      <div className="p-3 border-t border-border/30 flex items-center justify-between bg-muted/20">
        <p className="text-[10px] text-muted-foreground">
          Source: Official government fee schedules · {brandName}
        </p>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={handleTwitter} className="h-7 w-7 p-0">
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
          </Button>
          <Button variant="ghost" size="sm" onClick={handleCopy} className="h-7 w-7 p-0">
            {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
          </Button>
          <Link to={`/international/code-explorer/${procedureCode}`}>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
              <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function InternationalViralCards() {
  const brand = useBrand();

  const { data: intlData, isLoading } = useQuery({
    queryKey: ['international-stats-viral'],
    queryFn: () => loadStaticData('international_stats.json'),
    staleTime: 1000 * 60 * 60,
  });

  const countries = useMemo(() => {
    if (!intlData) return new Map<string, CountryInfo>();
    const map = new Map<string, CountryInfo>();
    ((intlData as any).countries as CountryInfo[] || []).forEach(c => map.set(c.country_code, c));
    return map;
  }, [intlData]);

  const procedureNames = useMemo(() => {
    if (!intlData) return new Map<string, ProcedureName>();
    const map = new Map<string, ProcedureName>();
    ((intlData as any).procedureNames as ProcedureName[] || []).forEach(p => map.set(p.code, p));
    return map;
  }, [intlData]);

  const usPrices = useMemo(() => {
    if (!intlData) return new Map<string, number>();
    const map = new Map<string, number>();
    ((intlData as any).usPricing as USPrice[] || []).forEach(p => map.set(p.cpt_code, p.avg_gross_charge));
    return map;
  }, [intlData]);

  const intlPriceMap = useMemo(() => {
    if (!intlData) return new Map<string, Map<string, number>>();
    const map = new Map<string, Map<string, number>>();
    ((intlData as any).procedurePrices as ProcedurePrice[] || []).forEach(p => {
      if (!map.has(p.us_code)) map.set(p.us_code, new Map());
      map.get(p.us_code)!.set(p.country_code, p.price_usd);
    });
    return map;
  }, [intlData]);

  // Build card data
  const cards = useMemo(() => {
    return VIRAL_PROCEDURES
      .map(code => {
        const proc = procedureNames.get(code);
        const usP = usPrices.get(code);
        const intlPrices = intlPriceMap.get(code);
        if (!proc || !usP || !intlPrices || usP <= 0) return null;

        const topCountries: { flag: string; name: string; price: number; savings: number; code: string }[] = [];
        intlPrices.forEach((price, cc) => {
          if (price > 0 && price < usP) {
            const country = countries.get(cc);
            if (country) {
              topCountries.push({
                flag: country.flag_emoji,
                name: country.country_name,
                price,
                savings: Math.round(((usP - price) / usP) * 100),
                code: cc,
              });
            }
          }
        });

        topCountries.sort((a, b) => a.price - b.price);

        if (topCountries.length < 3) return null;

        return {
          code,
          name: proc.llm_quick_name || proc.name,
          usPrice: usP,
          topCountries,
        };
      })
      .filter(Boolean) as NonNullable<ReturnType<typeof VIRAL_PROCEDURES.map>[0]>[];
  }, [procedureNames, usPrices, intlPriceMap, countries]);

  const handleShare = useCallback((text: string, url: string) => {
    if (navigator.share) {
      navigator.share({ text, url }).catch(() => {});
    } else {
      // Desktop browsers without the Web Share API: copy instead of no-op
      navigator.clipboard?.writeText(`${text} ${url}`)
        .then(() => toast.success('Copied to clipboard'))
        .catch(() => {});
    }
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Same Procedure, 32 Prices — Shareable Healthcare Cards | {brand.name}</title>
        <meta name="description" content="See how much the US overcharges for common procedures compared to 32 countries. Share these facts on social media." />
        <meta property="og:title" content="Same Procedure, 32 Prices — US vs The World" />
        <meta property="og:description" content="The US charges 10-50x more than other countries for the same procedures. See the data." />
        <link rel="canonical" href={`${brand.primaryDomain}/international/share`} />
      </Helmet>

      {/* Hero */}
      <section className="border-b border-border/40 bg-gradient-to-b from-red-500/5 to-background">
        <div className="max-w-7xl mx-auto px-4 py-10 sm:py-14 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 text-red-400 text-sm mb-4">
            <Share2 className="w-4 h-4" />
            Share the Truth
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-4">
            Same Procedure.{' '}
            <span className="text-red-400">32 Prices.</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Every card below uses real data from official government fee schedules.
            Share them, screenshot them, post them. The world needs to see this.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {isLoading ? (
          <div className="text-center py-20">
            <Globe className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading global pricing data...</p>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {cards.map(card => (
                <ViralCard
                  key={card.code}
                  procedureCode={card.code}
                  procedureName={card.name}
                  usPrice={card.usPrice}
                  topCountries={card.topCountries}
                  brandName={brand.name}
                  brandUrl={brand.primaryDomain}
                  onShare={handleShare}
                />
              ))}
            </div>

            {cards.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <p>No card data available yet.</p>
              </div>
            )}

            {/* CTA */}
            <Card className="mt-12 bg-primary/5 border-primary/20">
              <CardContent className="p-8 text-center">
                <h2 className="text-xl font-bold mb-2">Want to compare a specific procedure?</h2>
                <p className="text-muted-foreground mb-6">
                  Search any of {procedureNames.size.toLocaleString()} medical codes and see prices across 32 countries.
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  <Link to="/international/calculator">
                    <Button className="gap-2">
                      <TrendingDown className="w-4 h-4" />
                      Scenario Calculator
                    </Button>
                  </Link>
                  <Link to="/international/code-explorer">
                    <Button variant="outline" className="gap-2">
                      <Globe className="w-4 h-4" />
                      Code Explorer
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
